import React, {
  useEffect,
  useRef,
  useState,
} from "react";
import { FaLinkedinIn, FaWhatsapp } from "react-icons/fa";

import { Link } from "react-router-dom";
import { HashLink } from "react-router-hash-link";

import "./ConstantComponent.css";

/* ==========================================================
   FOOTER BLUE CURSOR NETWORK
========================================================== */

const FooterCursorNetwork = () => {
  const canvasRef = useRef(null);
  const footerRef = useRef(null);
  const animationRef = useRef(null);

  const mouseRef = useRef({
    x: -1000,
    y: -1000,
    targetX: -1000,
    targetY: -1000,
    active: false,
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    const footer = footerRef.current;

    if (!canvas || !footer) {
      return;
    }

    const ctx = canvas.getContext("2d");

    if (!ctx) {
      return;
    }

    let width = 0;
    let height = 0;
    let dpr = 1;

    let nodes = [];

    /* -------------------------------------------------------
       CREATE NETWORK NODES
    ------------------------------------------------------- */

    const createNodes = () => {
      const count = Math.max(
        30,
        Math.min(
          55,
          Math.floor(
            (width * height) / 30000
          )
        )
      );

      nodes = Array.from(
        { length: count },
        (_, index) => ({
          x: Math.random() * width,
          y: Math.random() * height,

          baseX:
            Math.random() * width,

          baseY:
            Math.random() * height,

          radius:
            index % 6 === 0
              ? 2.2
              : 1.2,

          phase:
            Math.random() *
            Math.PI *
            2,

          speed:
            0.0003 +
            Math.random() * 0.0004,
        })
      );
    };

    /* -------------------------------------------------------
       RESIZE
    ------------------------------------------------------- */

    const resize = () => {
      const rect =
        footer.getBoundingClientRect();

      width = rect.width;
      height = rect.height;

      dpr = Math.min(
        window.devicePixelRatio || 1,
        2
      );

      canvas.width =
        Math.floor(width * dpr);

      canvas.height =
        Math.floor(height * dpr);

      canvas.style.width =
        `${width}px`;

      canvas.style.height =
        `${height}px`;

      ctx.setTransform(
        dpr,
        0,
        0,
        dpr,
        0,
        0
      );

      createNodes();
    };

    /* -------------------------------------------------------
       MOUSE
    ------------------------------------------------------- */

    const handleMouseMove = (event) => {
      const rect =
        footer.getBoundingClientRect();

      const x =
        event.clientX - rect.left;

      const y =
        event.clientY - rect.top;

      const inside =
        x >= 0 &&
        x <= width &&
        y >= 0 &&
        y <= height;

      if (!inside) {
        mouseRef.current.active =
          false;

        return;
      }

      mouseRef.current.targetX = x;
      mouseRef.current.targetY = y;
      mouseRef.current.active = true;
    };

    const handleMouseLeave = () => {
      mouseRef.current.active =
        false;

      mouseRef.current.targetX =
        -1000;

      mouseRef.current.targetY =
        -1000;
    };

    /* -------------------------------------------------------
       DRAW NETWORK
    ------------------------------------------------------- */

    const draw = (time) => {
      ctx.clearRect(
        0,
        0,
        width,
        height
      );

      const mouse =
        mouseRef.current;

      /* Smooth cursor */
      mouse.x +=
        (mouse.targetX - mouse.x) *
        0.08;

      mouse.y +=
        (mouse.targetY - mouse.y) *
        0.08;

      /* ---------------------------------------------------
         GENTLE NODE MOVEMENT
      --------------------------------------------------- */

      nodes.forEach((node) => {
        const driftX =
          Math.sin(
            time * node.speed +
            node.phase
          ) * 8;

        const driftY =
          Math.cos(
            time * node.speed +
            node.phase
          ) * 7;

        node.x +=
          (
            node.baseX +
            driftX -
            node.x
          ) * 0.012;

        node.y +=
          (
            node.baseY +
            driftY -
            node.y
          ) * 0.012;
      });

      const networkDistance = 145;
      const cursorDistance = 210;

      /* ---------------------------------------------------
         BACKGROUND NETWORK CONNECTIONS
      --------------------------------------------------- */

      for (
        let i = 0;
        i < nodes.length;
        i++
      ) {
        for (
          let j = i + 1;
          j < nodes.length;
          j++
        ) {
          const a = nodes[i];
          const b = nodes[j];

          const dx =
            a.x - b.x;

          const dy =
            a.y - b.y;

          const distance =
            Math.sqrt(
              dx * dx +
              dy * dy
            );

          if (
            distance >
            networkDistance
          ) {
            continue;
          }

          const opacity =
            (
              1 -
              distance /
                networkDistance
            ) * 0.10;

          ctx.beginPath();

          ctx.moveTo(
            a.x,
            a.y
          );

          ctx.lineTo(
            b.x,
            b.y
          );

          ctx.strokeStyle =
            `rgba(
              75,
              145,
              255,
              ${opacity}
            )`;

          ctx.lineWidth = 0.7;

          ctx.stroke();
        }
      }

      /* ---------------------------------------------------
         CURSOR INTERACTION
      --------------------------------------------------- */

      if (mouse.active) {

        nodes.forEach((node) => {
          const dx =
            node.x -
            mouse.x;

          const dy =
            node.y -
            mouse.y;

          const distance =
            Math.sqrt(
              dx * dx +
              dy * dy
            );

          if (
            distance >
            cursorDistance
          ) {
            return;
          }

          const influence =
            1 -
            distance /
              cursorDistance;

          /* Cursor → node line */

          ctx.beginPath();

          ctx.moveTo(
            mouse.x,
            mouse.y
          );

          ctx.lineTo(
            node.x,
            node.y
          );

          ctx.strokeStyle =
            `rgba(
              65,
              160,
              255,
              ${influence * 0.55}
            )`;

          ctx.lineWidth =
            0.8 +
            influence * 0.7;

          ctx.stroke();

          /* Nearby node glow */

          ctx.beginPath();

          ctx.arc(
            node.x,
            node.y,
            node.radius +
              influence * 1.8,
            0,
            Math.PI * 2
          );

          ctx.fillStyle =
            `rgba(
              90,
              175,
              255,
              ${0.25 +
              influence * 0.55}
            )`;

          ctx.shadowColor =
            "rgba(50,150,255,.9)";

          ctx.shadowBlur =
            8 +
            influence * 12;

          ctx.fill();

          ctx.shadowBlur = 0;
        });

        /* -------------------------------------------------
           CURSOR HALO
        ------------------------------------------------- */

        const halo =
          ctx.createRadialGradient(
            mouse.x,
            mouse.y,
            0,
            mouse.x,
            mouse.y,
            125
          );

        halo.addColorStop(
          0,
          "rgba(45,145,255,.18)"
        );

        halo.addColorStop(
          0.45,
          "rgba(45,145,255,.07)"
        );

        halo.addColorStop(
          1,
          "rgba(45,145,255,0)"
        );

        ctx.beginPath();

        ctx.arc(
          mouse.x,
          mouse.y,
          125,
          0,
          Math.PI * 2
        );

        ctx.fillStyle = halo;

        ctx.fill();

        /* -------------------------------------------------
           MAIN CURSOR NODE
        ------------------------------------------------- */

        ctx.beginPath();

        ctx.arc(
          mouse.x,
          mouse.y,
          4,
          0,
          Math.PI * 2
        );

        ctx.fillStyle =
          "#72C4FF";

        ctx.shadowColor =
          "#3198FF";

        ctx.shadowBlur = 22;

        ctx.fill();

        ctx.shadowBlur = 0;
      }

      /* ---------------------------------------------------
         DRAW NETWORK NODES
      --------------------------------------------------- */

      nodes.forEach((node) => {
        const dx =
          node.x -
          mouse.x;

        const dy =
          node.y -
          mouse.y;

        const distance =
          Math.sqrt(
            dx * dx +
            dy * dy
          );

        const influence =
          mouse.active &&
          distance <
            cursorDistance
            ? 1 -
              distance /
                cursorDistance
            : 0;

        ctx.beginPath();

        ctx.arc(
          node.x,
          node.y,
          node.radius +
            influence * 1.5,
          0,
          Math.PI * 2
        );

        ctx.fillStyle =
          `rgba(
            80,
            165,
            255,
            ${0.22 +
            influence * 0.55}
          )`;

        ctx.shadowColor =
          "rgba(50,150,255,.8)";

        ctx.shadowBlur =
          influence > 0
            ? 12
            : 4;

        ctx.fill();

        ctx.shadowBlur = 0;
      });

      animationRef.current =
        requestAnimationFrame(draw);
    };

    /* -------------------------------------------------------
       INITIALIZE
    ------------------------------------------------------- */

    resize();

    window.addEventListener(
      "mousemove",
      handleMouseMove
    );

    window.addEventListener(
      "resize",
      resize
    );

    footer.addEventListener(
      "mouseleave",
      handleMouseLeave
    );

    animationRef.current =
      requestAnimationFrame(draw);

    /* -------------------------------------------------------
       CLEANUP
    ------------------------------------------------------- */

    return () => {
      window.removeEventListener(
        "mousemove",
        handleMouseMove
      );

      window.removeEventListener(
        "resize",
        resize
      );

      footer.removeEventListener(
        "mouseleave",
        handleMouseLeave
      );

      if (animationRef.current) {
        cancelAnimationFrame(
          animationRef.current
        );
      }
    };
  }, []);

  return (
    <div
      ref={footerRef}
      className="footer-cursor-network"
      aria-hidden="true"
    >
      <canvas ref={canvasRef} />
    </div>
  );
};


/* ==========================================================
   NAVBAR
========================================================== */

export const Navbar = () => {
  const [click, setClick] =
    useState(false);

  function handleClick() {
    if (window.innerWidth > 900) {
      return;
    }

    if (click) {
      document.body.style.overflowY =
        "auto";
    } else {
      document.body.style.overflowY =
        "hidden";
    }

    setClick(!click);
  }

  return (
    <>
      <nav className="navbar">

        <div className="navbar-container">

          <Link
            to="/"
            className="logo-link"
          >

            <img
              src="/ipr_logo.png"
              className="logo logo--nav"
              alt="IPR Technology"
            />

            <div className="logo-text">

              <span className="logo-title">
                IPR Technology
              </span>

              <span className="logo-subtitle">
                Powering a Sustainable Future
              </span>

            </div>

          </Link>


          <div
            className="menu-icon"
            onClick={handleClick}
          >
            <span className="material-symbols-outlined">
              {click
                ? "close"
                : "menu"}
            </span>
          </div>


          <ul
            className={
              click
                ? "nav-menu active"
                : "nav-menu"
            }
          >

            <li className="nav-item">

              <HashLink
                smooth
                to="/#home"
                className="nav-links"
                onClick={handleClick}
              >
                Home
              </HashLink>

            </li>


            <li className="nav-item">

              <HashLink
                smooth
                to="/#about"
                className="nav-links"
                onClick={handleClick}
              >
                About Us
              </HashLink>

            </li>


            <li className="nav-item">

              <HashLink
                smooth
                to="/#services"
                className="nav-links"
                onClick={handleClick}
              >
                Services
              </HashLink>

            </li>


            <li className="nav-item">

              <HashLink
                smooth
                to="/#expertise"
                className="nav-links"
                onClick={handleClick}
              >
                Expertise
              </HashLink>

            </li>


            <li className="nav-item">

              <HashLink
                smooth
                to="/#achievements"
                className="nav-links"
                onClick={handleClick}
              >
                Achievements
              </HashLink>

            </li>


            <li className="nav-item">

              <HashLink
                smooth
                to="/#clients"
                className="nav-links"
                onClick={handleClick}
              >
                Clients
              </HashLink>

            </li>


            <li className="nav-item">

              <HashLink
                smooth
                to="/#products"
                className="nav-links"
                onClick={handleClick}
              >
                Our Products
              </HashLink>

            </li>


            <li className="nav-item">

              <Link
                to="/team"
                className="nav-links"
                onClick={handleClick}
              >
                Our Team
              </Link>

            </li>


            <li className="nav-item">

              <HashLink
                smooth
                to="/#contact"
                className="contact-btn"
                onClick={handleClick}
              >
                Get In Touch
              </HashLink>

            </li>

          </ul>

        </div>

      </nav>
    </>
  );
};


/* ==========================================================
   FOOTER
========================================================== */

export const Footer = () => {

  return (
  <>
    <footer
      className="full-width"
      id="footer"
    >

      {/* BLUE CURSOR NETWORK */}
      <FooterCursorNetwork />


      {/* ==================================================
          BRAND
      ================================================== */}
<div className="footer-ipr">

  <Link
    to="/"
    className="footer-logo-link"
  >

    <img
      src="/ipr_logo.png"
      className="logo footer"
      alt="IPR Technology"
    />

  </Link>


  <p>
    Build the future of{" "}
    <span className="highlight">
      Power System
    </span>{" "}
    with us
  </p>


  {/* SOCIAL MEDIA */}
  <div className="footer-socials">

    <a
      href="https://www.linkedin.com/company/ipr-technologies/"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="LinkedIn"
    >
      <FaLinkedinIn />
    </a>


    

  </div>

</div>


      {/* ==================================================
          CONTACT
      ================================================== */}

      <div className="footer-contact">

        <p className="footer-contact-heading">
          Contact Us
        </p>


        <table>

          <tbody>

            <tr>

              <td>

                <p>
                  <span className="material-symbols-outlined">
                    apartment
                  </span>
                </p>

              </td>

              <td>

                <p>
                  Honnam complex, 2nd floor,
                  Ratna Vilas Road,
                  <br />
                  Basavanagudi, Bengaluru
                </p>

              </td>

            </tr>


            <tr>

              <td>

                <p>
                  <span className="material-symbols-outlined">
                    phone
                  </span>
                </p>

              </td>

              <td>

                <p>
                  +91 9902668672
                </p>

              </td>

            </tr>


            <tr>

              <td>

                <p>
                  <span className="material-symbols-outlined">
                    mail
                  </span>
                </p>

              </td>

              <td>

                <p>
                  info@iprtechnologies.com
                </p>

              </td>

            </tr>

          </tbody>

        </table>

      </div>


      {/* ==================================================
          QUICK LINKS
      ================================================== */}

      <div className="footer-contact">

        <p className="footer-contact-heading">
          Quick Links
        </p>


        <HashLink
          smooth
          to="/#home"
        >
          <p>Home</p>
        </HashLink>


        <HashLink
          smooth
          to="/#products"
        >
          <p>Products</p>
        </HashLink>


        <HashLink
          smooth
          to="/#services"
        >
          <p>Services</p>
        </HashLink>


        <HashLink
          smooth
          to="/#expertise"
        >
          <p>Expertise</p>
        </HashLink>


        <HashLink
          smooth
          to="/#achievements"
        >
          <p>Achievements</p>
        </HashLink>


        <Link to="/team">
          <p>Team</p>
        </Link>


        <Link
          to="/privacy-policy"
          onClick={() =>
            window.scrollTo({
              top: 0,
              behavior: "smooth",
            })
          }
        >
          <p>
            Privacy Policy
          </p>
        </Link>

      </div>


      {/* ==================================================
          COPYRIGHT
      ================================================== */}

{/* ==================================================
    COPYRIGHT
================================================== */}

    </footer>

    <div className="footer-copyright-section">
      <div className="footer-copyright">
        © 2026 IPR Technology. All Rights Reserved.
      </div>
    </div>

  </>
  );
};